import { isNil } from "lodash";
import PropTypes from "prop-types";
import React from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import FormSegmentControl from "../../components/form/FormSegmentControl";
import { withScreenContext } from "../../components/screen/ScreenContext";
import filterMatchOptions from "../../constants/filterMatchOptions";
import { GLOBAL_FILTER_ID, GLOBAL_FILTER_PANEL, LOCAL_FILTER_PANEL } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { isScreenModeMobile } from "../../constants/screenModes";
import dialogs from "../../dialogs";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { calendarSelectors } from "../../modules/definition/calendar";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import displayCacheSelectors from "../../modules/display/selectors";
import { filterOperations, filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { narrativeSelectors } from "../../modules/narrative";
import { savedFilterOperations, savedFilterSelectors } from "../../modules/savedFilters";
import { tagSelectors } from "../../modules/tags";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import CurrentFilterView from "./CurrentFilterView";
import FilterPanelHeader from "./FilterPanelHeader";

const renderGlobalSegmentControl = useProps => {
  const globalSegmentOptions = [
    {
      label: "All Views",
      value: GLOBAL_FILTER_PANEL,
      badgeCount: useProps.countGlobalActiveFilterGroups,
      badgeColor: "statusValid",
      interfaceIdentifier: interfaceIdentifiers.FILTER_PANEL_ALL_VIEWS
    },
    {
      label: "Focused View",
      value: LOCAL_FILTER_PANEL,
      badgeCount: useProps.countLocalActiveFilterGroups,
      badgeColor: "statusValid",
      interfaceIdentifier: interfaceIdentifiers.FILTER_PANEL_FOCUSED_VIEW
    }
  ];

  return (
    <div className="filterGlobalWrapper">
      <FormSegmentControl
        options={globalSegmentOptions}
        value={useProps.filterMode}
        onChange={useProps.onChangeFilterClick}
        pulsateIconId={useProps.pulsateIconId}
      />
    </div>
  );
};

const getFilterIdFromState = (state, props, isMobile) => {
  const { assignedFilterId } = props;
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  const filterMode = appStateSelectors.getLeftPanelMode(state.present) || GLOBAL_FILTER_ID;

  let filterId = assignedFilterId; // Assigned filter id first
  if (!filterId) {
    // Global filter id second
    if (filterMode === GLOBAL_FILTER_PANEL) {
      filterId = GLOBAL_FILTER_ID;
    } else {
      // active view id third
      filterId = activeViewId;
      // Mobile and Harvard, use mobileViewType
      if (isMobile) {
        //#!if harvard === true
        const mobileViewType = appStateSelectors.getMobilePortraitViewType(state.present);
        filterId = mobileViewType;
        //#!endif
      }
    }
  }

  return filterId;
};

const FilterPanel = props => {
  const style = props.style ? props.style : {};
  //#!if mobile === true
// style.paddingLeft = props.paddingLeft;
// style.paddingRight = props.paddingRight;
  //#!endif

  let showGlobalSegmentControl = true;
  let filterId = props.filterId;
  let filter = props.filter;

  //#!if mobile === true
// showGlobalSegmentControl = false;
  //#!endif

  //#!if harvard === true
  showGlobalSegmentControl = false;
  if (isScreenModeMobile(props.screenContext.screenMode)) {
    filterId = props.mobileFilterId;
    filter = props.mobileFilter;
  }
  //#!endif

  const onToggleGroup = (groupId, state) => props.onGroupToggled(filterId, groupId, state);

  const onLoadFilter = savedFilterId => props.onLoadFilter(props.filterId, savedFilterId);
  const onDeleteFilter = (savedFilterId, savedFilterLabel) => {
    dialogs.okCancelPrompt(`Are you sure you want to delete saved filter "${savedFilterLabel}"?`, result => {
      if (result) {
        props.onDeleteFilter(savedFilterId);
      }
    });
  };

  return (
    <div className="filterPanel" style={style}>
      <FilterPanelHeader {...props} filterId={filterId} onLoadFilter={onLoadFilter} onDeleteFilter={onDeleteFilter} />
      {showGlobalSegmentControl ? renderGlobalSegmentControl(props) : null}
      <CurrentFilterView {...props} filterId={filterId} filter={filter} onGroupToggled={onToggleGroup} />
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  const filterMode = appStateSelectors.getLeftPanelMode(state.present) || GLOBAL_FILTER_ID;
  const filterId = getFilterIdFromState(state, props);

  let mobileFilterId = null;
  //#!if harvard === true
  mobileFilterId = getFilterIdFromState(state, props, true);
  //#!endif

  return {
    filterMode,
    filterId,
    activeViewId,
    filter: filterSelectors.getFilterWithId(state.present, filterId),
    filterIsActive: Boolean(filterSelectors.isFilterWithIdActive(state.present, filterId)),
    countActiveFilterGroups: filterSelectors.countActiveFilterGroups(state.present, filterId),
    countGlobalActiveFilterGroups: filterSelectors.countActiveFilterGroups(state.present, GLOBAL_FILTER_ID),
    countLocalActiveFilterGroups: filterSelectors.countActiveFilterGroups(state.present, activeViewId),
    collapsedGroups: filterSelectors.collapsedGroups(state.present, filterId),
    allFilterGroups: filterSelectors.getAllFilterGroups(state.present),
    exposedFilterTypes: appStateSelectors.getExposedFilterTypes(state.present),
    savedFilters: savedFilterSelectors.getSavedFilters(state.present),
    savedFiltersExpanded: uiStateSelectors.getSavedFiltersExpanded(state.present),
    expandedGroups: appStateSelectors.getExpandedFilterGroups(state.present, filterId),
    colors: colorSelectors.getColors(state.present),
    types: typeSelectors.getOrderedTypes(state.present),
    tags: tagSelectors.getTags(state.present),
    references: referenceSelectors.getOrderedReferences(state.present),
    properties: propertySelectors.getOrderedProperties(state.present),
    itemIdsByType: itemSelectors.getItemIdsByType(state.present),
    displayItemsById: displayCacheSelectors.getDisplayItemsById(state.present),
    narrativeIds: narrativeSelectors.getNarrativeTreeIds(state.present),
    calendar: calendarSelectors.getCalendar(state.present),

    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present),

    //#!if harvard === true
    mobileFilterId,
    mobileFilter: filterSelectors.getFilterWithId(state.present, mobileFilterId),
    disableSplitView: appStateSelectors.getDisableSplitView(state.present),
    disableFilteringMore: appStateSelectors.getDisableFilteringMore(state.present)
    //#!endif
  };
};

const readonlyActions = {
  onCloseClick: appStateOperations.hideLeftPanel,
  onChangeFilterClick: newMode => {
    if (newMode === GLOBAL_FILTER_PANEL) {
      return appStateOperations.toggleGlobalFilterPanel();
    } else {
      return appStateOperations.toggleLocalFilterPanel();
    }
  },
  onFilterClearClick: filterOperations.clearFilter,
  onLoadFilter: filterOperations.loadFilter,
  onChangeFilterMatchCriteria: filterOperations.changeFilterMatchCriteria,
  onColorFilterChange: (filterId, terms) => {
    return filterOperations.changeColorFilter(filterId, terms, filterMatchOptions.ANY);
  },
  onTypeFilterChange: (filterId, terms) => {
    return filterOperations.changeTypeFilter(filterId, terms, filterMatchOptions.ANY);
  },
  onLabelFilterChange: filterOperations.changeLabelFilter,
  onSummaryFilterChange: filterOperations.changeSummaryFilter,
  onDateFilterChange: (filterId, above, below) => {
    return filterOperations.changeDateFilter(
      filterId,
      above && isNil(above.timestamp) ? null : above,
      below && isNil(below.timestamp) ? null : below
    );
  },
  onDurationFilterChange: filterOperations.changeDurationFilter,
  onNarrativeFilterChange: filterOperations.changeNarrativeFilter,
  onTagsFilterChange: filterOperations.changeTagsFilter,
  onTextPropertyChanged: filterOperations.changeTextPropertyFilter,
  onTypeReferenceFilterChanged: filterOperations.changeTypeReferenceFilter,
  onReferenceFilterChanged: filterOperations.changeReferenceFilter,
  onNumericPropertyChanged: filterOperations.changeNumericPropertyFilter,
  onBoolPropertyChanged: filterOperations.changeBoolPropertyFilter,
  onGroupToggled: (filterId, groupId, state) => {
    return state
      ? appStateOperations.expandFilterGroup(filterId, groupId)
      : appStateOperations.collapseFilterGroup(filterId, groupId);
  },
  onSavedFilterGroupToggled: state => {
    return state ? uiStateOperations.expandSavedFilters() : uiStateOperations.collapseSavedFilters();
  },
  setExposedFilterType: (filterId, state) => {
    return state
      ? appStateOperations.addExposedFilterType(filterId)
      : appStateOperations.removeExposedFilterType(filterId);
  }
};

const editingActions = {
  onSaveFilter: savedFilterOperations.saveFilter,
  onDeleteFilter: savedFilterOperations.deleteSavedFilter
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

FilterPanel.propTypes = {
  activeViewId: PropTypes.string,
  onCloseClick: PropTypes.func.isRequired,
  onChangeFilterClick: PropTypes.func.isRequired,
  onFilterClearClick: PropTypes.func.isRequired,
  onSaveFilter: PropTypes.func.isRequired,
  onDeleteFilter: PropTypes.func.isRequired,
  onLoadFilter: PropTypes.func.isRequired,
  onColorFilterChange: PropTypes.func.isRequired,
  onLabelFilterChange: PropTypes.func.isRequired,
  onSummaryFilterChange: PropTypes.func.isRequired,
  onDateFilterChange: PropTypes.func.isRequired,
  onDurationFilterChange: PropTypes.func.isRequired,
  onNarrativeFilterChange: PropTypes.func.isRequired,
  onTypeFilterChange: PropTypes.func.isRequired,
  onTypeReferenceFilterChanged: PropTypes.func.isRequired,
  onReferenceFilterChanged: PropTypes.func.isRequired,
  onTextPropertyChanged: PropTypes.func.isRequired,
  onNumericPropertyChanged: PropTypes.func.isRequired,
  onBoolPropertyChanged: PropTypes.func.isRequired,
  onGroupToggled: PropTypes.func.isRequired,
  onSavedFilterGroupToggled: PropTypes.func.isRequired,

  filterMode: PropTypes.string.isRequired,
  filterId: PropTypes.string.isRequired,
  filterIsActive: PropTypes.bool.isRequired,
  countActiveFilterGroups: PropTypes.number.isRequired,
  countGlobalActiveFilterGroups: PropTypes.number.isRequired,
  countLocalActiveFilterGroups: PropTypes.number.isRequired,
  savedFilters: ImmutablePropTypes.map.isRequired,
  savedFiltersExpanded: PropTypes.bool,
  colors: ImmutablePropTypes.listOf(PropTypeShapes.colorDefinition).isRequired,
  types: ImmutablePropTypes.listOf(PropTypeShapes.typeDefinition).isRequired,
  references: ImmutablePropTypes.listOf(PropTypeShapes.referenceDefinition).isRequired,
  properties: ImmutablePropTypes.listOf(PropTypeShapes.propertyDefinition).isRequired,
  expandedGroups: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemIdsByType: ImmutablePropTypes.map.isRequired,
  displayItemsById: ImmutablePropTypes.map.isRequired,
  narrativeIds: ImmutablePropTypes.list.isRequired,
  filter: PropTypeShapes.filter.isRequired,

  pulsateIconId: PropTypes.string,

  allFilterGroups: PropTypeShapes.listOptionList,
  exposedFilterTypes: ImmutablePropTypes.list,

  //#!if harvard === true
  mobileFilter: PropTypeShapes.filter,
  mobileFilterId: PropTypes.string,
  style: PropTypes.object,
  disableSplitView: PropTypes.bool,
  disableFilteringMore: PropTypes.bool,
  //#!endif

  // From withScreenContext
  screenContext: PropTypes.object,

  // From withContainerContext
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
};

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(withScreenContext(FilterPanel)));
